import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, User, ArrowRight, MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", value, onChange, required, testid, textarea }) => (
  <label className="block">
    <span className="font-accent text-xs uppercase tracking-widest text-zinc-500">{label}</span>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={4}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-white text-lg outline-none focus:border-[#D4AF37] transition-colors duration-300 resize-none placeholder:text-zinc-700"
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-white text-lg outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-zinc-700"
      />
    )}
  </label>
);

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const mapsUrl = "https://maps.google.com/?q=Via+Baccanello+35+Terno+d%27Isola+BG";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-40 bg-[#0d0d0d] border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-accent text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37]">{t.contact.overline}</span>
            <h2 className="mt-6 font-display text-4xl md:text-6xl font-medium tracking-tight text-white leading-none">
              {t.contact.title}
            </h2>
            <p className="mt-8 text-base md:text-lg text-zinc-400 leading-relaxed max-w-md">{t.contact.sub}</p>
          </Reveal>

          <Reveal delay={0.15} className="mt-12">
            <span className="font-accent text-xs uppercase tracking-widest text-zinc-500">{t.contact.directTitle}</span>
            <div className="mt-6 space-y-5">
              <div className="flex items-center gap-4 text-zinc-200">
                <User size={18} className="text-[#D4AF37]" />
                <span className="text-lg">Cristiano Olmi</span>
              </div>
              <a href="tel:+393462179997" data-testid="contact-phone-1" className="flex items-center gap-4 text-zinc-200 hover:text-[#D4AF37] transition-colors">
                <Phone size={18} className="text-[#D4AF37]" />
                <span className="text-lg">346 2179997</span>
              </a>
              <a href="tel:+39035903812" data-testid="contact-phone-2" className="flex items-center gap-4 text-zinc-200 hover:text-[#D4AF37] transition-colors">
                <Phone size={18} className="text-[#D4AF37]" />
                <span className="text-lg">035 903812</span>
              </a>
              <a href="mailto:info@olmisrl.it" data-testid="contact-email" className="flex items-center gap-4 text-zinc-200 hover:text-[#D4AF37] transition-colors">
                <Mail size={18} className="text-[#D4AF37]" />
                <span className="text-lg">info@olmisrl.it</span>
              </a>
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-address" className="flex items-start gap-4 text-zinc-200 hover:text-[#D4AF37] transition-colors">
                <MapPin size={18} className="text-[#D4AF37] mt-1 shrink-0" />
                <span className="text-lg">{t.contact.address}</span>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label={t.contact.name} name="name" value={form.name} onChange={onChange} required testid="contact-name-input" />
                <Field label={t.contact.email} name="email" type="email" value={form.email} onChange={onChange} required testid="contact-email-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label={t.contact.company} name="company" value={form.company} onChange={onChange} testid="contact-company-input" />
                <Field label={t.contact.phone} name="phone" value={form.phone} onChange={onChange} testid="contact-phone-input" />
              </div>
              <Field label={t.contact.message} name="message" value={form.message} onChange={onChange} required textarea testid="contact-message-input" />

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="btn-fill group inline-flex items-center gap-3 border border-[#D4AF37] px-8 py-4 font-accent text-sm uppercase tracking-widest text-[#D4AF37] hover:text-black disabled:opacity-50"
              >
                {loading ? t.contact.sending : t.contact.send}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-16 lg:mt-24">
        <Reveal>
          <div className="overflow-hidden clip-frame border border-white/10">
            <iframe
              title="Sede Olmi Srl — Via Baccanello 35, Terno d'Isola (BG)"
              data-testid="contact-map"
              src="https://maps.google.com/maps?q=Via%20Baccanello%2035%2C%20Terno%20d'Isola%20BG&z=15&output=embed"
              width="100%"
              height="420"
              style={{ border: 0, filter: "grayscale(0.6) invert(0.9) contrast(0.9)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
